import { Validators } from '@angular/forms';

import { InputBase, InputCheckBox, InputLabel, InputPanel } from '@ta/form-model';
import { isNonNullable } from '@ta/utils';

import { UserFieldVisibility } from '../dto/tenant';

export interface MkFieldOptions<T = unknown> {
  key: string;
  label: string;
  icon: string;
  class: string;
  visibleKey: string;
  visibleValue: boolean;
  field: InputBase<T>;
}

const FIELD_PANEL_CLASS = 'card highlight-title';

export function mkField<T>(options: MkFieldOptions<T>): InputPanel {
  const isRequired = options.field.validators?.includes(Validators.required) ?? false;
  return new InputPanel({
    key: `panel_${options.key}`,
    class: options.class,
    containerClass: [FIELD_PANEL_CLASS],
    contentClass: 'flex-column g-space-md',
    children: [
      new InputPanel({
        key: `panel_${options.key}_header`,
        contentClass: 'space-between align-center g-space-md',
        children: [
          new InputLabel({
            key: `label_${options.key}`,
            label: options.label,
            icon: options.icon,
            level: 4,
            required: isRequired,
          }),
          // L'interrupteur seul ne dit pas ce qu'il commande.
          new InputCheckBox({
            key: options.visibleKey,
            offLabel: 'app.forms.common.visibility.private',
            onLabel: 'app.forms.common.visibility.public',
            toggle: true,
            value: options.visibleValue,
          }),
        ],
      }),
      new InputPanel({
        key: `panel_${options.key}_content`,
        class: 'full',
        containerClass: ['no-title-space'],
        children: [options.field],
      }),
    ],
  });
}

/** Visibilité enregistrée d'un champ ; `defaultValue` tant que l'utilisateur n'a rien réglé. */
export function getFieldVisibility(
  visibilities: UserFieldVisibility[] | null | undefined,
  fieldName: string,
  defaultValue = true,
): boolean {
  return visibilities?.find((v) => v.fieldName === fieldName)?.isPublic ?? defaultValue;
}

/** Les interrupteurs `<champ>_visible` du formulaire, au format attendu par l'API. */
export function formatFieldVisibilities(data: Record<string, unknown>): UserFieldVisibility[] {
  return Object.entries(data)
    .filter(([key, value]) => key.endsWith('_visible') && isNonNullable(value))
    .map(([key, value]) => ({
      fieldName: key.replace('_visible', ''),
      isPublic: value === true || value === 'true',
    }));
}
